import { cn } from "@/utils/helpers"
import { cva } from "class-variance-authority"
import React, {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
} from "react"
import { useRef, useState } from "react"
import { TFunction } from "next-i18next"
import { InputField, InputFieldProps } from "./InputField"
import { Icon } from "../icon/Icon"

export const InputContainerVariants = cva("flex flex-col gap-1", {
  variants: {
    variant: {
      default: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

export interface InputProps extends Omit<InputFieldProps, "hasError"> {
  label?: string
  subText?: string
  hasSubTextIcon?: boolean
  error?: string
  containerClassName?: string
  t?: TFunction
  hasErrorIcon?: boolean
}
export const Input = forwardRef<
  HTMLInputElement,
  InputProps & {
    before?: string
    tooltipText?: string
  }
>(
  (
    {
      id,
      label,
      subText,
      hasSubTextIcon,
      className,
      containerClassName,
      t,
      tooltipText,
      error,
      onChange,
      ...props
    },
    ref,
  ) => {
    const [localValue, setLocalValue] = useState<
      React.InputHTMLAttributes<HTMLInputElement>["value"]
    >(props.defaultValue)
    const isControlled = useRef(false)
    useLayoutEffect(() => {
      setLocalValue(props.value)
      if (props.value !== undefined) {
        isControlled.current = true
      }
    }, [props.value])
    // props?.value ?? props?.defaultValue ?? "",
    const [tooltipPos, setTooltipPos] = useState<{
      x: number
      y: number
    }>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current)
    const inputId = useId()

    const handleMouseMove = (e: React.MouseEvent<HTMLInputElement>) => {
      const rect = e.currentTarget.getBoundingClientRect()
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      })
    }

    const handleMouseLeave = () => setTooltipPos(null)

    document.body.addEventListener("wheel", (ev) => {
      const target = ev.composedPath()[0] as HTMLElement
      if (target.matches('input[type="number"]')) {
        target.blur()
      }
    })

    const helperText = useMemo<React.ReactNode>(() => {
      if (error) {
        return (
          <div
            id={inputId + "subtext"}
            className="text-error flex items-center gap-1 text-sm"
            role="alert"
          >
            <Icon icon="dangerous" size="1rem" fill />
            <p className="leading-none">{t ? t(error) : error}</p>
          </div>
        )
      }
      if (subText && !error) {
        return (
          <span
            id={inputId + "subtext"}
            className="text-on-surface-variant flex items-center gap-1 text-sm"
          >
            {hasSubTextIcon && <Icon icon="info" size="1.125rem" />}
            <p className="">{subText}</p>
          </span>
        )
      }
    }, [error, hasSubTextIcon, inputId, subText, t])

    const limitText = useMemo<React.ReactNode>(() => {
      if (props.maxLength) {
        return (
          <p className="text-on-surface-variant text-sm leading-none">{`${typeof localValue === "number" ? 0 : localValue?.length || 0}/${props.maxLength}`}</p>
        )
      }
    }, [localValue, props.maxLength])

    return (
      <div
        className={cn(
          "group/input relative flex w-full flex-col gap-1",
          containerClassName,
        )}
        // onMouseMove={handleMouseMove}
        // onMouseLeave={handleMouseLeave}
      >
        {label && (
          <label
            className={cn(
              "text-on-surface-variant text-md w-fit text-left",
              error && "text-error",
            )}
            htmlFor={id ?? inputId}
          >
            {label}
          </label>
        )}
        <InputField
          id={id ?? inputId}
          hasError={!!error}
          className={className}
          aria-describedby={helperText ? inputId + "subtext" : undefined}
          onChange={(e) => {
            onChange?.(e)
            if (!isControlled.current) {
              setLocalValue(e.target.value)
            }
          }}
          {...props}
          ref={inputRef}
        />
        <div
          className={cn(
            "flex w-full cursor-default flex-row justify-between px-4",
            !helperText && !limitText && "hidden",
            (error || hasSubTextIcon) && "text-error pl-0",
          )}
        >
          {helperText}
          {limitText}
        </div>
        {tooltipPos && tooltipText && (
          <p
            className="bg-on-surface pointer-events-none absolute z-50 rounded px-2 py-1 text-sm text-white"
            style={{
              left: tooltipPos.x + 15,
              top: tooltipPos.y + 20,
            }}
          >
            {tooltipText}
          </p>
        )}
      </div>
    )
  },
)
Input.displayName = "Input"
